import { Button, Col, Row, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { Empty } from 'src/components/Empty/Empty';
import { ListPage } from 'src/components/FormPages/ListPage';
import { LoanApplicationStatus } from 'src/components/LoanApplicationStatus/LoanApplicationStatus';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { LoanApplicationDto } from 'src/generated-api-client';
import { ApplicationsStore } from 'src/stores/ApplicationsStore/ApplicationsStore';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';

export const ApplicationsPage = observer(() => {
    return (
        <ListPage
            pageName="Applications"
            store={ApplicationsStore as BasicStore<any>}
            empty={
                <Empty
                    description={t(
                        'Page.LoanApplicationsPage.Empty',
                        'No applications yet',
                    )}
                />
            }
            header={
                <Row gutter={16}>
                    <Col flex={1}>
                        <Typography.Title level={3}>
                            {t(
                                'Page.LoanApplicationsPage.Title',
                                'My applications',
                            )}
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            onClick={() => {
                                const link =
                                    EntityLinks.loanApplications.create?.();

                                link && Router.navigate(link);
                            }}
                        >
                            {t(
                                'Page.LoanApplicationsPage.Create',
                                'Create Application',
                            )}
                        </Button>
                    </Col>
                </Row>
            }
            renderItem={(item: Entity) => {
                const application = item as unknown as LoanApplicationDto;

                return (
                    <Card
                        key={application.id}
                        onClick={() => {
                            const path =
                                application.id !== undefined
                                    ? EntityLinks.loanApplications.itemDetails?.(
                                          application.id,
                                      )
                                    : undefined;

                            if (path) {
                                Router.navigate(path);
                            }
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'Page.ApplicationsPage.ListItem.ApplicationID',
                                        'Application ID',
                                    )}
                                    value={application.number}
                                />
                            </Col>
                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'Page.ApplicationsPage.ListItem.CreatedAt',
                                        'Created At',
                                    )}
                                    value={application.createdAt}
                                    formatValue="dateTimeSeconds"
                                />
                            </Col>
                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'Page.ApplicationsPage.ListItem.Status',
                                        'Status',
                                    )}
                                    value={application.status}
                                    formatValue={(status) => {
                                        return (
                                            <LoanApplicationStatus
                                                status={status}
                                            />
                                        );
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'Page.ApplicationsPage.ListItem.OfferName',
                                        'Offer Name',
                                    )}
                                    value={application?.offerName}
                                />
                            </Col>
                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'Page.ApplicationsPage.ListItem.Partner',
                                        'Partner',
                                    )}
                                    value={
                                        application.assignedPartner
                                            ?.arabicName ??
                                        t(
                                            'Page.ApplicationsPage.ListItem.PartnerNotAssigned',
                                            'Not assigned yet',
                                        )
                                    }
                                />
                            </Col>

                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'Page.ApplicationsPage.ListItem.Amount',
                                        'Amount',
                                    )}
                                    value={
                                        application?.finalOffer?.financingAmount
                                    }
                                    formatValue="currency-sar"
                                />
                            </Col>
                        </Row>
                    </Card>
                );
            }}
        />
    );
});
